import React, { useEffect } from "react"
import { Box, Button, Paper, TextField, Typography, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { ModeloIzzaGPTActionsTypes } from "../context/modeloIzzaGPTActions"
import { useModeloIzzaGPTContext } from "../context/useModeloIzzaGPTContext"

const MAX_CARACTERES = 12000

const PALAVRAS_CHAVE = [
    { chave: "$cardapio", descricao: "link de pedidos do delivery" },
    { chave: "$horarios", descricao: "horários de atendimento do delivery" },
    { chave: "$formaspagamento", descricao: "formas de pagamento do delivery" },
    { chave: "$telefone", descricao: "telefone da loja" },
    { chave: "$nomeloja", descricao: "nome da loja" },
    { chave: "$enderecoloja", descricao: "endereço da loja" },
]

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 4px 15px rgba(76,175,80,0.3)",
        padding: theme.spacing(1.2, 4),
        "&:hover": {
            backgroundColor: "#43a047",
            boxShadow: "0 6px 20px rgba(76,175,80,0.4)",
        },
    },
    contagem: {
        color: "#7f8c8d",
        fontSize: "0.85rem",
        marginTop: theme.spacing(0.5),
    },
    contagemAlerta: {
        color: "#e74c3c",
        fontSize: "0.85rem",
        marginTop: theme.spacing(0.5),
    },
    palavrasChave: {
        marginTop: theme.spacing(2),
    },
    palavraChaveItem: {
        display: "flex",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    chave: {
        color: "#4caf50",
        fontWeight: 600,
        fontFamily: "monospace",
        minWidth: 160,
    },
    descricao: {
        color: "#7f8c8d",
    },
}))

export function ModeloIzzaGPTComponent() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, erroCarregamento, salvar } = useModeloIzzaGPTContext()
    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const caracteres = dados.modelo.length
    const ultrapassou = caracteres > MAX_CARACTERES

    const handleSalvar = () => {
        if (ultrapassou) {
            enqueueSnackbar(`O modelo não pode ultrapassar ${MAX_CARACTERES} caracteres.`, { variant: "error" })
            return
        }
        salvar()
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant={isMobile ? "h5" : "h4"} className={classes.titleText} gutterBottom>
                    Modelo Izza GPT
                </Typography>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 24 }}>
                    Configure o modelo para funcionamento da Inteligência Artificial da Izza GPT.
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Descrição do Modelo
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={20}
                            value={dados.modelo}
                            onChange={e =>
                                dispatch({
                                    type: ModeloIzzaGPTActionsTypes.ATUALIZAR_MODELO,
                                    payload: e.target.value,
                                })
                            }
                            inputProps={{ maxLength: MAX_CARACTERES + 1000 }}
                        />
                        <Typography className={ultrapassou ? classes.contagemAlerta : classes.contagem}>
                            {caracteres} / {MAX_CARACTERES} caracteres
                        </Typography>

                        <Box className={classes.palavrasChave}>
                            <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 8 }}>
                                O texto pode usar as seguintes palavras-chave:
                            </Typography>
                            {PALAVRAS_CHAVE.map(({ chave, descricao }) => (
                                <Box key={chave} className={classes.palavraChaveItem}>
                                    <Typography component="span" className={classes.chave}>
                                        {chave}
                                    </Typography>
                                    <Typography component="span" variant="body2" className={classes.descricao}>
                                        {descricao}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>

                <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
